import React, { useEffect, useState } from 'react';
import PageLoader from '../PageLoader';
import { fetchSubscriptionPlans } from '../../services/subscription';
import { axiosInstanceV2 } from '../../services/axios';
import { toast } from 'react-toastify';
import { usePayment } from '../../hooks/usePayment';
import { useDispatch, useSelector } from 'react-redux';
import PricingCard from '../../pages/subscriptionPlan/components/PricingCard';
import { useNavigate } from 'react-router-dom';
import { setBillingType, setPlan } from '../../store/reducers/subscriptionSlice/subscriptionSlice';

function PricingModal({ isOpen, handleClose }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { openSubscriptionCheckout } = usePayment();
  const subscription = useSelector((state) => state?.userInfo?.userInfo?.data?.subscription)
  const isFreeUser = subscription?.is_free;
  const is_recurring = subscription?.is_recurring
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [billing, setBilling] = useState(subscription?.plan?.period ?? 'monthly');
  const [confirmModal, setConfirmModal] = useState(null);
  const [processing, setProcessing] = useState(false);


  const getPlans = async () => {
    try {
      setLoading(true);
      const response = await fetchSubscriptionPlans();
      setPlans(response.plans);
    } catch (error) {
      toast.error('Failed to fetch subscription plans');
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) getPlans();
  }, [isOpen]);

  // ── handle plan selection ───────────────────────────────────────
  const handleSelectPlan = (plan, type) => {
    console.log({ plan, type })
    setConfirmModal({ plan, type });
  };

  // ── confirm upgrade ──────────────────────────────────────────────
  const handleConfirmPlanChange = async () => {
    setProcessing(true);
    const { plan } = confirmModal;

    if (isFreeUser) {
      dispatch(setPlan(plan));
      dispatch(setBillingType(billing));

      setConfirmModal(null);
      handleClose();

      navigate("/checkout");

      return;
    }

    const activeBilling = plan.billing.find((b) => b.period === billing);

    if (!activeBilling) {
      toast.error('Plan not available for selected billing period');
      return;
    }

    let result
    try {
      const authToken = localStorage.getItem('authToken');
      const response = await axiosInstanceV2.post(
        '/subscription/change-plan',
        {
          razorpay_plan_id: activeBilling.razorpay_plan_id,
          billing_period: billing,
          is_recurring: is_recurring,
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      result = response.data.data;

      if (result.type === 'upgrade') {
        await openSubscriptionCheckout({
          subscription: result.subscription,
          billingDetails: result.billing_details,
          paymentMethod: subscription?.payment_details?.method,
        });
      }

      if (result.type === 'downgrade_scheduled') {
        toast.success(
          `Downgrade to ${plan.name} scheduled for ${new Date(result.effective_date).toLocaleDateString()}`
        );

      }

    } catch (error) {
      toast.error(error?.response?.data?.message ?? 'Failed to update subscription');
    } finally {
      setProcessing(false);
      setConfirmModal(null);
      handleClose();
      if (result.type === 'downgrade_scheduled') {
        window.location.reload()
      }
    }
  };

  const filteredPlans = plans?.filter((plan) => plan?.name?.toLowerCase() !== 'free')


  useEffect(() => {
    if (subscription?.plan?.period) {
      setBilling(subscription.plan.period);
    }
  }, [subscription]);


  if (!isOpen) return null;

  return (
    <>
      <div
        className="modal fade show d-block"
        tabIndex="-1"
        aria-modal="true"
        role="dialog"
        style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      >
        <div className="modal-dialog modal-dialog-top modal-xl">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                Change Subscription Plan
              </h5>
              <button type="button" className="btn-close" onClick={handleClose} />
            </div>

            <div className="modal-body w-100">
              {loading ? (
                <PageLoader />
              ) : (
                <div className="pricing-grid">
                  {filteredPlans?.map((plan, i) => (
                    <PricingCard
                      key={i}
                      plan={plan}
                      billing={billing}
                      currentPlanKey={subscription?.plan?.key}
                      currentRazorpayPlanId={subscription?.plan?.razorpay_plan_id}
                      onSelectPlan={handleSelectPlan}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button type="button" className="btn-cancel" onClick={handleClose}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── confirmation modal ──────────────────────────────────── */}
      {confirmModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1060 }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {isFreeUser
                    ? "Subscribe to Plan"
                    : confirmModal.type === "upgrade"
                      ? "Confirm Upgrade"
                      : "Confirm Downgrade"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setConfirmModal(null)}
                />
              </div>

              <div className="modal-body">
                <div className="modal-body">
                  {isFreeUser ? (
                    <p>
                      You're subscribing to the
                      <strong> {confirmModal.plan.name}</strong> plan.
                      Click Continue to provide your billing details before completing payment.
                    </p>
                  ) : confirmModal.type === "upgrade" ? (
                    <p>
                      After successful payment, your
                      <strong> {confirmModal.plan.name}</strong> plan will become active
                      immediately.
                    </p>
                  ) : (
                    <p>
                      You're downgrading to
                      <strong> {confirmModal.plan.name}</strong>.
                      This change will take effect at the end of your current billing cycle.
                    </p>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setConfirmModal(null)}
                >
                  Cancel
                </button>
                <button
                  disabled={processing}
                  type="button"
                  className="btn-primary-modal"
                  onClick={handleConfirmPlanChange}
                >
                  {isFreeUser
                    ? "Continue"
                    : confirmModal.type === "upgrade"
                      ? "Proceed to Payment"
                      : "Confirm Downgrade"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default PricingModal;